import { useEffect, useMemo, useState } from 'react';

import { DndProvider } from 'react-dnd';

import { IPolicy, IPolicyTableColumns, usePolicies } from '@entities/policy';
import ModalOpenBtn from '@features/modal-open-btn';
import { FilterInput } from '@features/table-filter';
import { Alert } from '@mui/material';
import ErrorMessage from '@shared/components/ErrorMessage';
import Loading from '@shared/components/Loading';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { Pagination, PaginationRows } from '@shared/components/Pagination';
import {
  Table,
  TBody,
  THead,
  TableActions,
  TableActionsItem,
  TableContainer,
  TableProvider,
} from '@shared/components/Table';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useTableSettings } from './settings/useTableSettings';
import './policies.css';

const PoliciesWidget = () => {
  const {
    policies,
    meta,
    isError,
    error,
    isLoading,
    isFetching,
    filter,
    updatePolicy,
    deletePolicy,
    clonePolicy,
    reorderRules,
    reorderPolicies,
  } = usePolicies();

  const [localOrderIds, setLocalOrderIds] = useState<string[] | null>(null);
  const policiesSignature = useMemo(
    () => policies.map(policy => `${policy.id}:${policy.priority}`).join('|'),
    [policies],
  );

  useEffect(() => {
    setLocalOrderIds(null);
  }, [policiesSignature]);

  const orderedPolicies = useMemo(() => {
    if (!localOrderIds) return policies;
    return reorderPoliciesByIds(policies, localOrderIds);
  }, [policies, localOrderIds]);

  const searchValue = filter.currentFilter.get('filter[name]')?.toLowerCase().trim() ?? '';
  const filteredPolicies = useMemo(() => {
    if (!searchValue) return orderedPolicies;
    return orderedPolicies.filter(policy => policy.name.toLowerCase().includes(searchValue));
  }, [orderedPolicies, searchValue]);

  const { columnTitles, renderItem, renderRow, makeNewPolicyModal } = useTableSettings({
    onDelete: deletePolicy,
    onToggle: updatePolicy,
    onClone: clonePolicy,
    onReorder: reorderPolicies,
    onReorderRules: reorderRules,
    policies,
    onLocalReorder: setLocalOrderIds,
  });

  const newPolicyModal = useMemo(() => makeNewPolicyModal(), [makeNewPolicyModal]);

  const tableFilter = {
    ...filter,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateFilter: (filters: any) => filter.updateFilter(filters),
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorMessage error={error} />;

  return (
    <DndProvider backend={HTML5Backend}>
      {isFetching && <Loading />}
      <Alert sx={{ mb: 2 }} severity='info'>
        Policies are processed in order of priority. Policies at the top have higher priority.
      </Alert>
      <TableProvider<IPolicyTableColumns> columnTitles={columnTitles} {...tableFilter}>
        <TableActions>
          <TableActionsItem>
            <FilterInput<IPolicyTableColumns> name='name' placeholder='Search policies' />
          </TableActionsItem>
          <TableActionsItem>
            <ModalOpenBtn text='New policy' modalContent={newPolicyModal} size='md' />
          </TableActionsItem>
        </TableActions>

        <TableContainer id='policies-table' className='policies-table-left'>
          <Table>
            <THead />
            <TBody items={filteredPolicies} renderItem={renderItem} renderRow={renderRow} />
          </Table>
        </TableContainer>
      </TableProvider>
      <div className='pagination-container'>
        <Pagination data={meta} />
        <PaginationRows />
      </div>
    </DndProvider>
  );
};

export default PoliciesWidget;

const reorderPoliciesByIds = (items: IPolicy[], ids: string[]) => {
  const map = new Map(items.map(policy => [policy.id, policy]));
  const ordered: IPolicy[] = [];
  ids.forEach((id, idx) => {
    const policy = map.get(id);
    if (!policy) return;
    ordered.push({ ...policy, priority: idx + 1 });
    map.delete(id);
  });
  map.forEach(policy => ordered.push({ ...policy, priority: ordered.length + 1 }));
  return ordered;
};
